import React, { useState } from 'react';
import { Card } from '@/components/common/Card';
import { Check, X, ChevronDown, ChevronUp } from 'lucide-react';
import { Module, Permission } from '@/config/permissions';

interface CustomPermission {
  module: Module;
  permissions: Permission[];
}

interface EditablePermissionMatrixProps {
  userPermissions: CustomPermission[];
  onChange: (permissions: CustomPermission[]) => void;
}

const moduleConfig = {
  dashboard: { name: 'Dashboard', color: 'indigo' },
  accounts: { name: 'Accounts', color: 'blue' },
  contacts: { name: 'Contacts', color: 'blue' },
  abm_journey: { name: 'ABM Journey', color: 'emerald' },
  call_management: { name: 'Call Management', color: 'purple' },
  mql_gate: { name: 'MQL Gate', color: 'amber' },
  magnets: { name: 'Magnets', color: 'cyan' },
  users: { name: 'User Management', color: 'rose' },
  prospecting: { name: 'Vibe Prospecting', color: 'emerald' },
  reports: { name: 'Reports', color: 'blue' }
};

const allPermissions: Permission[] = ['read', 'create', 'edit', 'delete', 'schedule', 'approve', 'reject'];

const permissionLabels: Record<Permission, string> = {
  read: 'View',
  create: 'Create',
  edit: 'Edit',
  delete: 'Delete',
  schedule: 'Schedule',
  approve: 'Approve',
  reject: 'Reject'
};

export const EditablePermissionMatrix: React.FC<EditablePermissionMatrixProps> = ({
  userPermissions,
  onChange
}) => {
  const [expandedModules, setExpandedModules] = useState<Module[]>([]);

  const toggleModule = (module: Module) => {
    setExpandedModules(prev =>
      prev.includes(module)
        ? prev.filter(m => m !== module)
        : [...prev, module]
    );
  };

  const hasPermission = (module: Module, permission: Permission): boolean => {
    const modulePerms = userPermissions.find(p => p.module === module);
    return modulePerms?.permissions.includes(permission) || false;
  };

  const togglePermission = (module: Module, permission: Permission) => {
    const modulePerms = userPermissions.find(p => p.module === module);
    
    if (!modulePerms) {
      // Add new module with this permission
      onChange([...userPermissions, { module, permissions: [permission] }]);
    } else {
      const hasIt = modulePerms.permissions.includes(permission);
      const newPerms = hasIt
        ? modulePerms.permissions.filter(p => p !== permission)
        : [...modulePerms.permissions, permission];

      if (newPerms.length === 0) {
        // Remove module if no permissions left
        onChange(userPermissions.filter(p => p.module !== module));
      } else {
        // Update permissions
        onChange(userPermissions.map(p =>
          p.module === module ? { ...p, permissions: newPerms } : p
        ));
      }
    }
  };

  const toggleAllPermissions = (module: Module, enable: boolean) => {
    if (enable) {
      const existingModule = userPermissions.find(p => p.module === module);
      if (existingModule) {
        onChange(userPermissions.map(p =>
          p.module === module ? { ...p, permissions: [...allPermissions] } : p
        ));
      } else {
        onChange([...userPermissions, { module, permissions: [...allPermissions] }]);
      }
    } else {
      onChange(userPermissions.filter(p => p.module !== module));
    }
  };

  const getPermissionCount = (module: Module): number => {
    return userPermissions.find(p => p.module === module)?.permissions.length || 0;
  };

  const getColorClasses = (color: string) => {
    const colors: Record<string, { bg: string; border: string; text: string; badge: string }> = {
      indigo: { bg: 'bg-indigo-50', border: 'border-indigo-200', text: 'text-indigo-700', badge: 'bg-indigo-100 text-indigo-700' },
      blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-700' },
      emerald: { bg: 'bg-emerald-50', border: 'border-emerald-200', text: 'text-emerald-700', badge: 'bg-emerald-100 text-emerald-700' },
      purple: { bg: 'bg-purple-50', border: 'border-purple-200', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-700' },
      amber: { bg: 'bg-amber-50', border: 'border-amber-200', text: 'text-amber-700', badge: 'bg-amber-100 text-amber-700' },
      cyan: { bg: 'bg-cyan-50', border: 'border-cyan-200', text: 'text-cyan-700', badge: 'bg-cyan-100 text-cyan-700' },
      rose: { bg: 'bg-rose-50', border: 'border-rose-200', text: 'text-rose-700', badge: 'bg-rose-100 text-rose-700' }
    };
    return colors[color] || colors.blue;
  };

  return (
    <div className="space-y-3">
      <div className="text-sm text-gray-600 mb-4">
        Click on permissions to enable/disable. Click module names to expand details.
      </div>

      {(Object.keys(moduleConfig) as Module[]).map(module => {
        const config = moduleConfig[module];
        const colors = getColorClasses(config.color);
        const isExpanded = expandedModules.includes(module);
        const permCount = getPermissionCount(module);
        const hasFullAccess = permCount === allPermissions.length;
        const hasNoAccess = permCount === 0;

        return (
          <Card key={module} className={`border ${colors.border} ${colors.bg}`}>
            {/* Module Header */}
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3 flex-1">
                  <button
                    onClick={() => toggleModule(module)}
                    className="flex items-center gap-2 hover:opacity-70"
                  >
                    {isExpanded ? (
                      <ChevronUp className="w-4 h-4 text-gray-500" />
                    ) : (
                      <ChevronDown className="w-4 h-4 text-gray-500" />
                    )}
                  </button>

                  <div className="flex-1">
                    <h3 className={`font-semibold ${colors.text}`}>{config.name}</h3>
                    <div className="flex items-center gap-2 mt-1">
                      <span className={`text-xs px-2 py-0.5 rounded-full ${colors.badge}`}>
                        {permCount} / {allPermissions.length} permissions
                      </span>
                    </div>
                  </div>
                </div>

                {/* Quick Toggle Buttons */}
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => toggleAllPermissions(module, true)}
                    disabled={hasFullAccess}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      hasFullAccess
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-green-100 text-green-700 hover:bg-green-200'
                    }`}
                  >
                    Full Access
                  </button>
                  <button
                    onClick={() => toggleAllPermissions(module, false)}
                    disabled={hasNoAccess}
                    className={`px-3 py-1.5 text-xs font-medium rounded-md transition-colors ${
                      hasNoAccess
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-red-100 text-red-700 hover:bg-red-200'
                    }`}
                  >
                    No Access
                  </button>
                </div>
              </div>

              {/* Expanded Permission Checkboxes */}
              {isExpanded && (
                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-3 gap-3">
                    {allPermissions.map(permission => {
                      const enabled = hasPermission(module, permission);
                      return (
                        <button
                          key={permission}
                          onClick={() => togglePermission(module, permission)}
                          className={`flex items-center gap-2 px-3 py-2 rounded-md border transition-all ${
                            enabled
                              ? 'border-green-300 bg-green-50 text-green-700'
                              : 'border-gray-200 bg-white text-gray-600 hover:border-gray-300'
                          }`}
                        >
                          <div className={`w-5 h-5 rounded flex items-center justify-center border ${
                            enabled
                              ? 'border-green-500 bg-green-500'
                              : 'border-gray-300 bg-white'
                          }`}>
                            {enabled && <Check className="w-3 h-3 text-white" />}
                          </div>
                          <span className="text-sm font-medium">{permissionLabels[permission]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          </Card>
        );
      })}
    </div>
  );
};
